<template>
  <div class="report-page">
    <!-- 筛选区 -->
    <div class="filter-bar">
      <div class="filter-left">
        <el-select v-model="storeId" clearable placeholder="全部粮库" style="width:220px" @change="loadData">
          <el-option v-for="s in stores" :key="s.id" :label="s.storeName" :value="s.id" />
        </el-select>
        <el-button type="primary" :icon="Refresh" @click="loadData" :loading="loading">刷新</el-button>
      </div>
      <div class="filter-right">
        <span class="update-time" v-if="updateTime">更新于 {{ updateTime }}</span>
      </div>
    </div>

    <!-- 总览卡片 -->
    <div class="card-row">
      <div class="stat-card" v-for="c in cardList" :key="c.key" :class="'card-' + c.key">
        <div class="stat-icon">{{ c.icon }}</div>
        <div class="stat-body">
          <div class="stat-value">{{ c.value }}</div>
          <div class="stat-label">{{ c.label }}</div>
        </div>
      </div>
    </div>

    <!-- 图表区 -->
    <el-row :gutter="16">
      <el-col :span="12">
        <div class="chart-card">
          <div class="chart-title">工单状态分布</div>
          <div ref="woStatusRef" class="chart-box"></div>
        </div>
      </el-col>
      <el-col :span="12">
        <div class="chart-card">
          <div class="chart-title">近 30 天工单 / 报警趋势</div>
          <div ref="trendRef" class="chart-box"></div>
        </div>
      </el-col>
    </el-row>

    <el-row :gutter="16">
      <el-col :span="12">
        <div class="chart-card">
          <div class="chart-title">报警类型分布</div>
          <div ref="alarmTypeRef" class="chart-box"></div>
        </div>
      </el-col>
      <el-col :span="12">
        <div class="chart-card">
          <div class="chart-title">各粮库报警数量 TOP10</div>
          <div ref="alarmTopRef" class="chart-box"></div>
        </div>
      </el-col>
    </el-row>

    <el-row :gutter="16">
      <el-col :span="12">
        <div class="chart-card">
          <div class="chart-title">合同到期倒计时（180 天内）</div>
          <div class="contract-list">
            <template v-if="stats.contractExpire && stats.contractExpire.length">
              <div class="contract-row" v-for="c in stats.contractExpire" :key="c.storeName + c.endDate">
                <div class="contract-store">{{ c.storeName }}</div>
                <div class="contract-info">
                  <span class="days-badge" :class="urgentClass(c.daysLeft)">{{ c.daysLeft }} 天</span>
                  <span class="end-date">{{ c.endDate }}</span>
                </div>
              </div>
            </template>
            <el-empty v-else description="暂无即将到期的合同" :image-size="60" />
          </div>
        </div>
      </el-col>
      <el-col :span="12">
        <div class="chart-card">
          <div class="chart-title">监控在线率（按粮库）</div>
          <div ref="monitorRef" class="chart-box"></div>
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { Refresh } from '@element-plus/icons-vue'
import * as echarts from 'echarts'
import { getDashboardStats, getGrainStoreList } from '../api'

const loading = ref(false)
const storeId = ref(null)
const stores = ref([])
const updateTime = ref('')
const stats = reactive({
  totalStores: 0, totalWarehouses: 0, totalMonitors: 0, onlineMonitors: 0,
  pendingAlarms: 0, monthWorkOrders: 0, monthAlarms: 0, totalContracts: 0, pendingPatrols: 0,
  workOrderStatus: [], trend30Days: [], alarmTypes: [], storeAlarmTop: [],
  contractExpire: [], monitorOnline: [], warehouseUsage: []
})

const woStatusRef = ref(null)
const trendRef = ref(null)
const alarmTypeRef = ref(null)
const alarmTopRef = ref(null)
const monitorRef = ref(null)
const charts = []

const cardList = computed(() => [
  { key: 'stores',   label: '粮库总数',     value: stats.totalStores,   icon: '🏛️' },
  { key: 'wh',       label: '仓房总数',     value: stats.totalWarehouses,icon: '🏭' },
  { key: 'mon',      label: '监控设备',     value: stats.totalMonitors,  icon: '📷' },
  { key: 'online',   label: '在线设备',     value: stats.onlineMonitors,icon: '✅' },
  { key: 'alarm',    label: '待处理报警',   value: stats.pendingAlarms, icon: '🚨' },
  { key: 'wo',       label: '本月工单',     value: stats.monthWorkOrders,icon: '📋' },
  { key: 'contract', label: '合同总数',     value: stats.totalContracts,icon: '📄' },
  { key: 'patrol',   label: '待巡检任务',   value: stats.pendingPatrols,icon: '🔍' }
])

function urgentClass(days) {
  if (days == null) return ''
  if (days <= 30) return 'urgent'
  if (days <= 90) return 'warn'
  return ''
}

async function loadData() {
  loading.value = true
  try {
    const [sRes, rRes] = await Promise.all([
      getGrainStoreList().catch(() => null),
      getDashboardStats(storeId.value ? { storeId: storeId.value } : {}).catch(() => null)
    ])
    if (sRes && sRes.data) stores.value = sRes.data
    if (rRes && rRes.data) {
      console.log('[Report] stats API response:', rRes.data)
      Object.assign(stats, rRes.data)
      updateTime.value = new Date().toLocaleTimeString()
    } else {
      console.warn('[Report] stats API failed, rRes=', rRes)
    }
    await nextTick()
    renderCharts()
  } finally {
    loading.value = false
  }
}

function renderCharts() {
  charts.forEach(c => c.dispose())
  charts.length = 0

  // 工单状态 — 环形图
  if (woStatusRef.value) {
    const map = { 0: '待处理', 1: '处理中', 2: '已完成', 3: '已关闭' }
    const colors = { 0: '#f56c6c', 1: '#e6a23c', 2: '#67c23a', 3: '#909399' }
    const data = (stats.workOrderStatus || []).map(r => ({
      name: map[r.name] || ('状态' + r.name),
      value: r.value,
      itemStyle: { color: colors[r.name] || '#409eff' }
    }))
    const c = echarts.init(woStatusRef.value)
    c.setOption({
      tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
      legend: { bottom: 0, icon: 'circle' },
      series: [{
        type: 'pie', radius: ['45%', '70%'], center: ['50%', '45%'],
        avoidLabelOverlap: false,
        label: { show: true, formatter: '{d}%', fontSize: 12 },
        labelLine: { show: false },
        data
      }]
    })
    charts.push(c)
  }

  // 30 天趋势 — 双线面积图
  if (trendRef.value) {
    const data = stats.trend30Days || []
    const c = echarts.init(trendRef.value)
    c.setOption({
      tooltip: { trigger: 'axis' },
      legend: { data: ['工单', '报警'], bottom: 0, icon: 'roundRect' },
      grid: { left: 40, right: 20, top: 20, bottom: 40 },
      xAxis: { type: 'category', data: data.map(d => d.date), axisLabel: { fontSize: 10 } },
      yAxis: { type: 'value', minInterval: 1 },
      series: [
        { name: '工单', type: 'line', smooth: true, data: data.map(d => d.workOrders),
          itemStyle: { color: '#409eff' }, areaStyle: { color: 'rgba(64,158,255,0.15)' } },
        { name: '报警', type: 'line', smooth: true, data: data.map(d => d.alarms),
          itemStyle: { color: '#f56c6c' }, areaStyle: { color: 'rgba(245,108,108,0.15)' } }
      ]
    })
    charts.push(c)
  }

  // 报警类型 — 饼图
  if (alarmTypeRef.value) {
    const typeMap = { 1: '温度异常', 2: '湿度异常', 3: '设备故障', 4: '入侵告警', 5: '烟雾告警' }
    const palette = ['#f56c6c', '#e6a23c', '#409eff', '#67c23a', '#909399', '#9b59b6']
    const data = (stats.alarmTypes || []).map((r, i) => ({
      name: typeMap[r.type] || ('类型' + r.type),
      value: r.value,
      itemStyle: { color: palette[i % palette.length] }
    }))
    const c = echarts.init(alarmTypeRef.value)
    c.setOption({
      tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
      legend: { bottom: 0, icon: 'circle' },
      series: [{
        type: 'pie', radius: '65%', center: ['50%', '45%'],
        label: { formatter: '{b}\n{c}起', fontSize: 11 },
        data
      }]
    })
    charts.push(c)
  }

  // 粮库报警 TOP10 — 横向条形图
  if (alarmTopRef.value) {
    const data = (stats.storeAlarmTop || []).slice().reverse()
    const c = echarts.init(alarmTopRef.value)
    c.setOption({
      tooltip: { trigger: 'axis' },
      grid: { left: 80, right: 30, top: 10, bottom: 20 },
      xAxis: { type: 'value', minInterval: 1 },
      yAxis: { type: 'category', data: data.map(d => d.storeName), axisLabel: { fontSize: 11 } },
      series: [{
        type: 'bar', data: data.map(d => d.count),
        itemStyle: {
          borderRadius: [0, 4, 4, 0],
          color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
            { offset: 0, color: '#409eff' }, { offset: 1, color: '#79bbff' }
          ])
        },
        label: { show: true, position: 'right', fontSize: 11 }
      }]
    })
    charts.push(c)
  }

  // 监控在线率 — 横向进度条
  if (monitorRef.value) {
    const data = (stats.monitorOnline || []).slice(0, 10).reverse()
    const c = echarts.init(monitorRef.value)
    c.setOption({
      tooltip: { trigger: 'axis', formatter: p => {
        const d = p[0]
        const online = data[data.length - 1 - d.dataIndex] || data[d.dataIndex]
        const rate = online.total ? Math.round(online.online / online.total * 100) : 0
        return `${d.name}<br/>在线 ${online.online} / ${online.total} (${rate}%)`
      }},
      grid: { left: 80, right: 30, top: 10, bottom: 20 },
      xAxis: { type: 'value', max: 100, axisLabel: { formatter: '{value}%' } },
      yAxis: { type: 'category', data: data.map(d => d.storeName), axisLabel: { fontSize: 11 } },
      series: [{
        type: 'bar',
        data: data.map(d => ({
          value: d.total ? Math.round(d.online / d.total * 100) : 0,
          itemStyle: {
            color: d.total && d.online / d.total >= 0.9 ? '#67c23a'
                 : d.total && d.online / d.total >= 0.7 ? '#e6a23c' : '#f56c6c',
            borderRadius: [0, 4, 4, 0]
          }
        })),
        label: { show: true, position: 'right', formatter: p => `${p.value}%`, fontSize: 11 }
      }]
    })
    charts.push(c)
  }
}

// 窗口 resize 自适应
let resizeHandler = null
onMounted(() => {
  loadData()
  resizeHandler = () => charts.forEach(c => c.resize())
  window.addEventListener('resize', resizeHandler)
})
onUnmounted(() => {
  charts.forEach(c => c.dispose())
  if (resizeHandler) window.removeEventListener('resize', resizeHandler)
})

watch(storeId, () => { if (storeId.value !== undefined) loadData() })
</script>

<style scoped>
.report-page { padding: 16px; }

.filter-bar {
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 16px; padding: 12px 16px;
  background: #fff; border-radius: 8px; box-shadow: 0 1px 4px rgba(0,0,0,.06);
}
.filter-left { display: flex; gap: 10px; align-items: center; }
.update-time { color: #909399; font-size: 12px; }

.card-row {
  display: grid; grid-template-columns: repeat(8, 1fr); gap: 12px;
  margin-bottom: 16px;
}
.stat-card {
  display: flex; align-items: center; gap: 12px;
  padding: 16px; background: #fff; border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0,0,0,.06);
  border-left: 3px solid transparent;
  transition: transform .2s;
}
.stat-card:hover { transform: translateY(-2px); }
.stat-card.card-stores   { border-left-color: #409eff; }
.stat-card.card-wh      { border-left-color: #67c23a; }
.stat-card.card-mon     { border-left-color: #909399; }
.stat-card.card-online  { border-left-color: #67c23a; }
.stat-card.card-alarm   { border-left-color: #f56c6c; }
.stat-card.card-wo      { border-left-color: #e6a23c; }
.stat-card.card-contract{ border-left-color: #9b59b6; }
.stat-card.card-patrol  { border-left-color: #1abc9c; }

.stat-icon { font-size: 28px; }
.stat-body { display: flex; flex-direction: column; }
.stat-value { font-size: 26px; font-weight: 700; color: #303133; line-height: 1.1; }
.stat-label { font-size: 12px; color: #909399; margin-top: 2px; }

.chart-card {
  background: #fff; border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0,0,0,.06);
  padding: 12px 16px 8px; margin-bottom: 16px;
}
.chart-title {
  font-size: 14px; font-weight: 600; color: #303133;
  margin-bottom: 8px; padding-left: 8px;
  border-left: 3px solid #409eff;
}
.chart-box { height: 280px; }

.contract-list { padding: 8px 0; max-height: 280px; overflow-y: auto; }
.contract-row {
  display: flex; justify-content: space-between; align-items: center;
  padding: 10px 12px; margin-bottom: 6px;
  background: #f7f9fc; border-radius: 6px;
}
.contract-store { font-weight: 600; color: #303133; font-size: 13px; }
.contract-info { display: flex; align-items: center; gap: 10px; }
.days-badge {
  padding: 2px 10px; border-radius: 10px; font-size: 12px; font-weight: 600;
  background: #e6a23c; color: #fff;
}
.days-badge.urgent { background: #f56c6c; }
.days-badge.warn   { background: #e6a23c; }
.end-date { font-size: 12px; color: #909399; }
</style>
